import * as constants from "./constants";

export interface ApiDecision {
    id: string;
    numero: string;
    juridiction: string;
    matiere: string;
    nature_litige: string;
    demandeur: string;
    defendeur: string;
    demandeur_domicile?: string;
    defendeur_domicile?: string;
    conseil_defense?: string;
    date_decision: string;
    statut: 'brouillon' | 'a_verifier' | 'valide' | 'anonymise' | 'publie' | 'rejete';
    agent: {
        id: string;
        first_name: string;
        last_name: string;
        email: string;
    };
    created_at: string;
}

export interface ApiDecisionDetail extends ApiDecision {
    fichier_presigned_url: string | null;
    contenu_original: string | null;
    contenu_anonymise: string | null;
    fichier_anonymise_presigned_url: string | null;
    entites_masquees: Record<string, string[]>;
    resume: string | null;
    mots_cles: string[] | null;
    // Nouveaux champs séparation marge/corps
    contenu_marge: string | null;
    contenu_marge_anonymise: string | null;
    contenu_corps: string | null;
    contenu_corps_anonymise: string | null;
    historiques: Array<{
        id: string;
        action: string;
        acteur_type: string;
        agent: {
            id: string;
            first_name: string;
            last_name: string;
            email: string;
        };
        statut_avant: string | null;
        statut_apres: string;
        details: any;
        created_at: string;
    }>;
    updated_at: string;
}

export interface UploadResult {
    fichier: string;
    success: boolean;
    decision_id?: string;
    task_id?: string;
    statut?: string;
    pipeline?: string[];
    polling_url?: string;
    error?: string;
}

export interface PipelineStep {
    action: string;
    acteur_type?: string;
    statut_avant: string | null;
    statut_apres: string;
    details?: any;
    created_at: string;
}

export interface DashboardStats {
    par_statut: Record<string, number>;
    ce_mois: number;
    cette_semaine: number;
    par_juridiction: Array<{ juridiction: string; total: number }>;
    volume_mensuel: Array<{ mois: string; total: number }>;
}

export interface AdminDashboardData {
    utilisateurs: { actifs: number; [key: string]: any };
    decisions: { en_attente: number; [key: string]: any };
    top_agents: Array<{ nom: string; total: number; [key: string]: any }>;
    alertes: {
        agents_inactifs: Array<{ id: string; nom: string; email: string }>;
        erreurs_pipeline: number;
        decisions_en_retard: number;
        [key: string]: any;
    };
    activite_recente: Array<{
        action: string;
        created_at: string;
        agent: string | null;
        decision_numero: string;
        [key: string]: any;
    }>;
    [key: string]: any;
}

export interface PipelineItem {
    id: string;
    decision_id: string;
    numero?: string;
    fichier?: string;
    statut: string;
    etape_courante?: string;
    progression?: number;
    created_at: string;
    updated_at?: string;
    [key: string]: any;
}

export interface PipelineStats {
    en_cours: number;
    termines_24h: number;
    en_erreur: number;
    [key: string]: any;
}

export interface SuperAdminOrganisation {
    id: string;
    nom: string;
    slug: string;
    actif: boolean;
    decisions_total: number;
    decisions_publiees: number;
    agents: number;
    signalements: number;
    taux_confiance_ocr: number | null;
    date_activation: string;
}

export interface SuperAdminDashboardData {
    stats: {
        organisations_actives: number;
        decisions_traitees: number;
        decisions_publiees: number;
        taux_publication: number;
        signalements_ouverts: number;
    };
    organisations: SuperAdminOrganisation[];
    evolution_decisions: Array<{
        mois: string;
        [key: string]: any;
    }>;
    comparatif?: Array<{
        id: string;
        nom: string;
        slug: string;
        volume_cumule: number;
        taux_publication: number;
        delai_moyen_jours: number | null;
        pourcentage_volume: number;
    }>;
    volume_mensuel?: Array<{
        mois: string;
        [key: string]: any;
    }>;
    decisions_par_statut?: Array<{
        id: string;
        nom: string;
        slug: string;
        brouillon: number;
        a_verifier: number;
        valide: number;
        anonymise: number;
        publie: number;
        rejete: number;
    }>;
    performance_agents?: Record<string, Array<{
        id: string;
        nom: string;
        decisions: number;
    }>>;
}

export interface DecisionsState {
    isLoading: boolean;
    decisions: ApiDecision[];
    count: number;
    next: string | null;
    previous: string | null;
    errors: any;
    // Detail
    detailLoading: boolean;
    detail: ApiDecisionDetail | null;
    detailError: any;
    // Submit actions
    isSubmitting: boolean;
    submitError: any;
    // Upload
    isUploading: boolean;
    uploadResults: UploadResult[];
    uploadError: any;
    // Workflow
    workflowLoading: boolean;
    workflowError: any;
    // Pipeline polling per decision
    pipelineSteps: Record<string, PipelineStep[]>;
    // Dashboard stats
    stats: DashboardStats | null;
    statsLoading: boolean;
    // Admin dashboard
    adminDashboard: AdminDashboardData | null;
    adminDashboardLoading: boolean;
    // Pipeline
    pipeline: PipelineItem[];
    pipelineStats: PipelineStats | null;
    pipelineLoading: boolean;
    // Reassign
    isReassigning: boolean;
    // Super-admin dashboard
    superAdminDashboard: SuperAdminDashboardData | null;
    superAdminDashboardLoading: boolean;
}

export const initialState: DecisionsState = {
    isLoading: false,
    decisions: [],
    count: 0,
    next: null,
    previous: null,
    errors: null,
    detailLoading: false,
    detail: null,
    detailError: null,
    isSubmitting: false,
    submitError: null,
    isUploading: false,
    uploadResults: [],
    uploadError: null,
    workflowLoading: false,
    workflowError: null,
    pipelineSteps: {},
    stats: null,
    statsLoading: false,
    adminDashboard: null,
    adminDashboardLoading: false,
    pipeline: [],
    pipelineStats: null,
    pipelineLoading: false,
    isReassigning: false,
    superAdminDashboard: null,
    superAdminDashboardLoading: false,
};

const decisionsReducer = (state = initialState, action: any): DecisionsState => {
    switch (action.type) {
        // List
        case constants.FETCH_DECISIONS_REQUESTING:
            return { ...state, isLoading: true, errors: null };
        case constants.FETCH_DECISIONS_SUCCESS:
            return {
                ...state,
                isLoading: false,
                decisions: action.payload.results,
                count: action.payload.count,
                next: action.payload.next,
                previous: action.payload.previous,
            };
        case constants.FETCH_DECISIONS_FAILED:
            return { ...state, isLoading: false, errors: action.error };

        // Detail
        case constants.FETCH_DECISION_DETAIL_REQUESTING:
            return { ...state, detailLoading: true, detailError: null };
        case constants.FETCH_DECISION_DETAIL_SUCCESS:
            return { ...state, detailLoading: false, detail: action.payload };
        case constants.FETCH_DECISION_DETAIL_FAILED:
            return { ...state, detailLoading: false, detailError: action.error };
        case constants.CLEAR_DECISION_DETAIL:
            return { ...state, detail: null, detailError: null };

        // Create / Update / Delete
        case constants.CREATE_DECISION_REQUESTING:
        case constants.UPDATE_DECISION_REQUESTING:
        case constants.DELETE_DECISION_REQUESTING:
            return { ...state, isSubmitting: true, submitError: null };
        case constants.CREATE_DECISION_SUCCESS:
            return { ...state, isSubmitting: false };
        case constants.UPDATE_DECISION_SUCCESS:
            return { ...state, isSubmitting: false };
        case constants.DELETE_DECISION_SUCCESS:
            return {
                ...state,
                isSubmitting: false,
                decisions: state.decisions.filter(d => d.id !== action.id),
                count: state.count - 1,
            };
        case constants.CREATE_DECISION_FAILED:
        case constants.UPDATE_DECISION_FAILED:
        case constants.DELETE_DECISION_FAILED:
            return { ...state, isSubmitting: false, submitError: action.error };

        // Upload
        case constants.UPLOAD_DECISIONS_REQUESTING:
            return { ...state, isUploading: true, uploadResults: [], uploadError: null };
        case constants.UPLOAD_DECISIONS_SUCCESS:
            return { ...state, isUploading: false, uploadResults: action.payload };
        case constants.UPLOAD_DECISIONS_FAILED:
            return { ...state, isUploading: false, uploadError: action.error };

        // Workflow
        case constants.WORKFLOW_ACTION_REQUESTING:
            return { ...state, workflowLoading: true, workflowError: null };
        case constants.WORKFLOW_ACTION_SUCCESS:
            return {
                ...state,
                workflowLoading: false,
                detail: action.payload,
                // Also update in list if present
                decisions: state.decisions.map(d =>
                    d.id === action.payload.id ? { ...d, statut: action.payload.statut } : d
                ),
            };
        case constants.WORKFLOW_ACTION_FAILED:
            return { ...state, workflowLoading: false, workflowError: action.error };

        // Pipeline polling
        case constants.POLL_HISTORIQUES_UPDATE:
            return {
                ...state,
                pipelineSteps: {
                    ...state.pipelineSteps,
                    [action.decisionId]: action.steps,
                },
            };

        // Stats
        case constants.FETCH_STATS_REQUESTING:
            return { ...state, statsLoading: true };
        case constants.FETCH_STATS_SUCCESS:
            return { ...state, statsLoading: false, stats: action.payload };
        case constants.FETCH_STATS_FAILED:
            return { ...state, statsLoading: false };

        // Admin dashboard
        case constants.FETCH_ADMIN_DASHBOARD_REQUESTING:
            return { ...state, adminDashboardLoading: true };
        case constants.FETCH_ADMIN_DASHBOARD_SUCCESS:
            return { ...state, adminDashboardLoading: false, adminDashboard: action.payload };
        case constants.FETCH_ADMIN_DASHBOARD_FAILED:
            return { ...state, adminDashboardLoading: false };

        // Pipeline
        case constants.FETCH_PIPELINE_REQUESTING:
            return { ...state, pipelineLoading: true };
        case constants.FETCH_PIPELINE_SUCCESS:
            return {
                ...state,
                pipelineLoading: false,
                pipeline: action.payload.decisions || action.payload.results || [],
                pipelineStats: action.payload.stats || null,
            };
        case constants.FETCH_PIPELINE_FAILED:
            return { ...state, pipelineLoading: false };

        // Reassign
        case constants.REASSIGN_DECISIONS_REQUESTING:
            return { ...state, isReassigning: true };
        case constants.REASSIGN_DECISIONS_SUCCESS:
            return { ...state, isReassigning: false };
        case constants.REASSIGN_DECISIONS_FAILED:
            return { ...state, isReassigning: false };

        // Super-admin dashboard
        case constants.FETCH_SUPERADMIN_DASHBOARD_REQUESTING:
            return { ...state, superAdminDashboardLoading: true };
        case constants.FETCH_SUPERADMIN_DASHBOARD_SUCCESS:
            return { ...state, superAdminDashboardLoading: false, superAdminDashboard: action.payload };
        case constants.FETCH_SUPERADMIN_DASHBOARD_FAILED:
            return { ...state, superAdminDashboardLoading: false };

        default:
            return state;
    }
};

export default decisionsReducer;
